import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import axios from "axios";

import ShopLayout from "./auth/ShopLayout";
import ShopHome from "./pages/shop/ShopHome";

import { AuthProvider } from "./auth/AuthContext";
import { CartProvider } from "./auth/CartContext";
import { WishlistProvider } from "./auth/WishlistContext";
import { SettingsProvider } from "./auth/SettingsContext";
import { TourProvider } from "./components/tour";
// iter205 v4 — AuthCallback component is no longer used as a route.
// The OAuth exchange has been moved into AuthProvider (runs silently
// in the background on app mount). Import retained as a no-op for any
// downstream references; can be safely deleted in a future cleanup.
// import AuthCallback from "./auth/AuthCallback";

import { Toaster } from "sonner";
import ScrollToTop from "./components/ScrollToTop";
import AnalyticsBeacon from "./components/AnalyticsBeacon";
import DialogHost from "./components/ConfirmDialog";
import BootHealthGate from "./components/BootHealthGate";
import BoutiqueLoader from "./components/shop/BoutiqueLoader";
import ErrorBoundary from "./components/ErrorBoundary";
import NoIndexRoute from "./components/seo/NoIndexRoute";

// Lazy-load all customer pages except home (home is critical above-the-fold)
const ItemDetail = lazy(() => import("./pages/shop/ItemDetail"));
const ItemReviews = lazy(() => import("./pages/shop/ItemReviews"));
const Cart = lazy(() => import("./pages/shop/Cart"));
const Checkout = lazy(() => import("./pages/shop/Checkout"));
const MyOrders = lazy(() => import("./pages/shop/MyOrders"));
const OrderDetail = lazy(() => import("./pages/shop/OrderDetail"));
const TrackParcel = lazy(() => import("./pages/shop/TrackParcel"));
const Wishlist = lazy(() => import("./pages/shop/Wishlist"));
const CategoryPage = lazy(() => import("./pages/shop/CategoryPage"));
const Sale = lazy(() => import("./pages/shop/Sale"));
// iter234 — Legal pages
const LegalPage = lazy(() => import("./pages/LegalPage"));
const CollectionLanding = lazy(() => import("./pages/shop/CollectionLanding"));
const Profile = lazy(() => import("./pages/shop/Profile"));
// iter 211 — JnsCreditPage hidden from customers (May 30, 2026); kept on backend for legacy refunds.
// const JnsCreditPage = lazy(() => import("./pages/shop/JnsCreditPage"));
const CustomerNotifications = lazy(() => import("./pages/shop/Notifications"));
const DepartmentLanding = lazy(() => import("./pages/shop/DepartmentLanding"));
const OffersPage = lazy(() => import("./pages/shop/OffersPage"));
const NotFound = lazy(() => import("./pages/shop/NotFound"));

// Phase 2A — Custom Studio customer routes
const CustomStudioLanding = lazy(() => import("./pages/shop/CustomStudio").then(m => ({ default: m.CustomStudioLanding })));
const CustomStudioDesigner = lazy(() => import("./pages/shop/CustomStudio").then(m => ({ default: m.CustomStudioDesigner })));
const SavedCustomDesigns = lazy(() => import("./pages/shop/CustomStudio").then(m => ({ default: m.SavedCustomDesigns })));
const SharedCustomDesign = lazy(() => import("./pages/shop/CustomStudio").then(m => ({ default: m.SharedCustomDesign })));
// iter289 — Visual Studio (Photo-first design mode). Parallel route to
// /custom-studio/design; the stepper is untouched.
const VisualStudioBuilder = lazy(() => import("./pages/shop/custom-studio/v4/visual/VisualStudioBuilder"));
const Lookbook = lazy(() => import("./pages/shop/Lookbook"));
const CustomCheckout = lazy(() => import("./pages/shop/CustomCheckout"));
const V4Checkout = lazy(() => import("./pages/shop/custom-studio/v4/V4Checkout"));
const CustomOrderSuccess = lazy(() => import("./pages/shop/CustomOrderSuccess"));
const CustomOrderDetail = lazy(() => import("./pages/shop/CustomOrderDetail"));
const AccountAddressesPage = lazy(() => import("./pages/shop/AccountAddressesPage"));
const SharedLookPage = lazy(() => import("./pages/SharedLookPage"));

// Support tickets (Phase 1 — May 2026)
const MyTickets = lazy(() => import("./pages/shop/MyTickets"));
const TicketDetail = lazy(() => import("./pages/shop/TicketDetail"));

// Internal QA preview — lazy-loaded so it doesn't bloat the customer bundle.
const ShipmentCardPreview = lazy(() => import("./pages/preview/ShipmentCardPreview"));

// Lazy-load the ENTIRE admin bundle so customer visitors never download it
const AdminRoutes = lazy(() => import("./admin/AdminRoutes"));

// Send credentials (cookies) with every API request
axios.defaults.withCredentials = true;

// Single shared QueryClient for the whole app
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 60 * 1000,
            gcTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 1,
        },
    },
});

// iter205 v4 — The OAuth callback handling has moved into AuthProvider
// (runs in the background on mount). ShopOrCallback used to swap to a
// dedicated <AuthCallback> overlay when `#session_id=...` was present,
// which created a jarring full-screen takeover. Now we just render
// ShopHome unconditionally; AuthProvider clears the hash + exchanges
// the session silently and AuthContext.user morphs into signed-in
// state when ready.
function ShopOrCallback() {
    return <ShopHome />;
}

function RouteFallback() {
    return (
        <BoutiqueLoader
            label="Stitching the page…"
            sublabel="Just a moment"
            className="min-h-[60vh]"
            data-testid="route-loader"
        />
    );
}

function App() {
    return (
        <div className="App">
            <Toaster position="bottom-center" richColors closeButton />
            <DialogHost />
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <BootHealthGate>
                    <ScrollToTop />
                    <AnalyticsBeacon />
                    <AuthProvider>
                        <CartProvider>
                            <WishlistProvider>
                                <SettingsProvider>
                                <TourProvider>
                                <Suspense fallback={<RouteFallback />}>
                                <Routes>
                                    {/* Internal preview routes — NOT linked from any nav. Used during dev to QA components without admin login. */}
                                    <Route path="/__preview/shipment-card" element={<NoIndexRoute title="Internal preview"><ShipmentCardPreview /></NoIndexRoute>} />
                                    {/* S5E — Public boutique tracker (no-auth, HMAC-tokenised). Lives OUTSIDE ShopLayout so we don't leak nav chrome onto a shared link. */}
                                    <Route path="/track/:shipment_number" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Parcel tracking"><TrackParcel /></NoIndexRoute></ErrorBoundary>} />
                                    {/* Customer storefront */}
                                    <Route element={<ShopLayout />}>
                                        <Route path="/" element={<ErrorBoundary scope="customer"><ShopOrCallback /></ErrorBoundary>} />
                                        <Route path="/d/:slug" element={<ErrorBoundary scope="customer"><DepartmentLanding /></ErrorBoundary>} />
                                        <Route path="/c/:category" element={<ErrorBoundary scope="customer"><CategoryPage /></ErrorBoundary>} />
                                        <Route path="/sale" element={<ErrorBoundary scope="customer"><Sale /></ErrorBoundary>} />
                                        <Route path="/privacy-policy" element={<ErrorBoundary scope="customer"><LegalPage slug="privacy-policy" /></ErrorBoundary>} />
                                        <Route path="/return-policy" element={<ErrorBoundary scope="customer"><LegalPage slug="return-policy" /></ErrorBoundary>} />
                                        <Route path="/terms" element={<ErrorBoundary scope="customer"><LegalPage slug="terms" /></ErrorBoundary>} />
                                        <Route path="/collection/:key" element={<ErrorBoundary scope="customer"><CollectionLanding /></ErrorBoundary>} />
                                        {/* Canonical SEO product URL. Legacy /item/:id is kept forever
                                            (old shares, emails, the shipped native app bundle) and
                                            self-redirects to the canonical path via ItemDetail. */}
                                        <Route path="/products/:slugId" element={<ErrorBoundary scope="customer"><ItemDetail /></ErrorBoundary>} />
                                        <Route path="/item/:id" element={<ErrorBoundary scope="customer"><ItemDetail /></ErrorBoundary>} />
                                        <Route path="/items/:id/reviews" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Product reviews"><ItemReviews /></NoIndexRoute></ErrorBoundary>} />
                                        <Route path="/cart" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Cart"><Cart /></NoIndexRoute></ErrorBoundary>} />
                                        <Route path="/offers" element={<ErrorBoundary scope="customer"><OffersPage /></ErrorBoundary>} />
                                        <Route path="/wishlist" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Wishlist"><Wishlist /></NoIndexRoute></ErrorBoundary>} />
                                        <Route path="/checkout" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Checkout"><Checkout /></NoIndexRoute></ErrorBoundary>} />
                                        <Route path="/orders" element={<ErrorBoundary scope="customer"><NoIndexRoute title="My orders"><MyOrders /></NoIndexRoute></ErrorBoundary>} />
                                        <Route path="/orders/:id" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Order details"><OrderDetail /></NoIndexRoute></ErrorBoundary>} />
                                        <Route path="/tickets" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Support tickets"><MyTickets /></NoIndexRoute></ErrorBoundary>} />
                                        <Route path="/tickets/:id" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Support ticket"><TicketDetail /></NoIndexRoute></ErrorBoundary>} />
                                        <Route path="/profile" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Profile"><Profile /></NoIndexRoute></ErrorBoundary>} />
                                        {/* iter 211 — /jns-credit hidden from customers (May 30, 2026).
                                            Redirect any bookmarked links to the profile page. */}
                                        <Route path="/jns-credit" element={<Navigate to="/profile" replace />} />
                                        <Route path="/notifications" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Notifications"><CustomerNotifications /></NoIndexRoute></ErrorBoundary>} />
                                        {/* Phase 2A — Custom Studio (master-flag aware) */}
                                        <Route path="/custom-studio" element={<ErrorBoundary scope="customer"><CustomStudioLanding /></ErrorBoundary>} />
                                        <Route path="/custom-studio/design" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Custom Studio design"><CustomStudioDesigner /></NoIndexRoute></ErrorBoundary>} />
                                        {/* iter289 — Visual Studio (parallel mode). Stepper untouched at /design. */}
                                        <Route path="/custom-studio/visual" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Visual Studio design"><VisualStudioBuilder /></NoIndexRoute></ErrorBoundary>} />
                                        <Route path="/custom-studio/shared/:token" element={<ErrorBoundary scope="customer"><SharedCustomDesign /></ErrorBoundary>} />
                                        <Route path="/lookbook" element={<ErrorBoundary scope="customer"><Lookbook /></ErrorBoundary>} />
                                        <Route path="/account/custom-designs" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Saved custom designs"><SavedCustomDesigns /></NoIndexRoute></ErrorBoundary>} />
                                        <Route path="/account/addresses" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Saved addresses"><AccountAddressesPage /></NoIndexRoute></ErrorBoundary>} />
                                        {/* Phase 3A — Custom checkout + JNSC orders */}
                                        <Route path="/custom-checkout/:designId" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Custom checkout"><CustomCheckout /></NoIndexRoute></ErrorBoundary>} />
                                        <Route path="/custom-checkout/v4/:draftId" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Custom checkout"><V4Checkout /></NoIndexRoute></ErrorBoundary>} />
                                        <Route path="/custom-orders/success/:orderId" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Custom order success"><CustomOrderSuccess /></NoIndexRoute></ErrorBoundary>} />
                                        <Route path="/custom-orders/:orderId" element={<ErrorBoundary scope="customer"><NoIndexRoute title="Custom order details"><CustomOrderDetail /></NoIndexRoute></ErrorBoundary>} />
                                        {/* May 26 2026 — public share landing page (no auth) */}
                                        <Route path="/share/look/:token" element={<ErrorBoundary scope="customer"><SharedLookPage /></ErrorBoundary>} />
                                        {/* Catch-all → boutique 404 (kept INSIDE ShopLayout so
                                            the customer keeps the header, footer and category
                                            nav even on a dead link). */}
                                        <Route path="*" element={<NotFound />} />
                                    </Route>

                                    {/* Admin portal — entire bundle code-split */}
                                    <Route path="/config/*" element={<ErrorBoundary scope="admin"><NoIndexRoute title="JnS Admin"><AdminRoutes /></NoIndexRoute></ErrorBoundary>} />
                                </Routes>
                                </Suspense>
                                </TourProvider>
                                </SettingsProvider>
                            </WishlistProvider>
                        </CartProvider>
                    </AuthProvider>
                    </BootHealthGate>
                </BrowserRouter>
            </QueryClientProvider>
        </div>
    );
}

export default App;
